import React from 'react';
import { useTranslation } from 'react-i18next';
import { PiGlobeLight, PiUsersLight } from 'react-icons/pi';

interface OrgWideToggleProps {
  id?: string;
  name?: string;
  label?: string;
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  /** Override the main label text */
  title?: string;
  /** Override the description when off */
  offDescription?: string;
  /** Override the description when on */
  onDescription?: string;
  /** Override the helper text when on */
  onHelperText?: string;
}

const OrgWideToggle: React.FC<OrgWideToggleProps> = ({
  value,
  onChange,
  disabled,
  title,
  offDescription,
  onDescription,
  onHelperText,
}) => {
  const { t } = useTranslation();

  const displayTitle = title || t('Organization-wide');
  const displayOffDesc = offDescription || t('Enable to share with everyone in your organization.');
  const displayOnDesc = onDescription || t('All members of your organization will have view access. You will remain the admin.');
  const displayOnHelper = onHelperText || t('All organization members will be added as viewers');

  return (
    <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="flex-shrink-0 mt-0.5">
        <button
          type="button"
          role="switch"
          aria-checked={value}
          disabled={disabled}
          onClick={() => onChange(!value)}
          className={`
            relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
            ${value
              ? 'bg-primary-600'
              : 'bg-gray-300 dark:bg-gray-600'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <span
            className={`
              inline-block h-4 w-4 transform rounded-full bg-white transition-transform
              ${value ? 'translate-x-6' : 'translate-x-1'}
            `}
          />
        </button>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <PiGlobeLight className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {displayTitle}
          </span>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {value ? displayOnDesc : displayOffDesc}
        </p>
        {value && (
          <div className="flex items-center gap-1.5 mt-2 text-xs text-primary-600 dark:text-primary-400">
            <PiUsersLight className="w-3.5 h-3.5" />
            <span>{displayOnHelper}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgWideToggle;
